import React, { useEffect, useMemo, useState } from 'react';
import { getMember, getMemberRoutines } from '../auth/authApi';
import { initials, avatarColor, timeAgo, fmtDate } from '../lib/consoleUtils';
import Spinner from './Spinner';

const sessionImages = (r) => [r.photo_url, ...(r.routine_drills || []).map((d) => d.photoUrl)].filter(Boolean);
const scoreOf = (r) => (r && r.analysis && typeof r.analysis.score === 'number' ? r.analysis.score : null);

const isDryFire = (r) => {
  if (!r) return false;
  const hay = [r.category, r.type, r.name, r.skills, r.level]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (/dry[\s_-]?fire/.test(hay)) return true;
  const drills = Array.isArray(r.routine_drills) ? r.routine_drills : [];
  if (drills.length > 0 && drills.every((d) => Number(d.totalLiveRounds || 0) === 0)) return true;
  return false;
};

function AnalysisBlock({ analysis }) {
  if (!analysis) return null;
  const { strengths, weaknesses, recommendations } = analysis;
  const has = (a) => Array.isArray(a) && a.length > 0;
  if (!has(strengths) && !has(weaknesses) && !has(recommendations)) return null;
  return (
    <>
      <div className="alist">
        {has(strengths) && <div className="abox good"><h4>Strengths</h4><ul>{strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
        {has(weaknesses) && <div className="abox weak"><h4>Areas to improve</h4><ul>{weaknesses.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
      </div>
      {has(recommendations) && <div className="abox"><h4>Recommendations</h4><ul>{recommendations.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
    </>
  );
}

export default function MemberDrawer({ userId, orgId, onClose }) {
  const [member, setMember] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [includeDryFire, setIncludeDryFire] = useState(true);
  const [sortBy, setSortBy] = useState('newest'); // newest | score-desc | score-asc

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(''); setMember(null); setRoutines([]); setSelected(null); setIncludeDryFire(true); setSortBy('newest');
    Promise.all([getMember(userId, orgId), getMemberRoutines(userId, orgId)])
      .then(([m, r]) => { if (alive) { setMember(m); setRoutines(r); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [userId, orgId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (lightbox) setLightbox(null);
      else if (selected) setSelected(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, selected, onClose]);

  const filteredRoutines = useMemo(() => {
    const list = includeDryFire ? [...routines] : routines.filter((r) => !isDryFire(r));
    if (sortBy === 'newest') {
      // API already returns newest first; keep that order.
      return list;
    }
    return list.sort((a, b) => {
      const sa = scoreOf(a);
      const sb = scoreOf(b);
      // Sessions without a score sink to the end for both directions.
      if (sa == null && sb == null) return 0;
      if (sa == null) return 1;
      if (sb == null) return -1;
      return sortBy === 'score-desc' ? sb - sa : sa - sb;
    });
  }, [routines, includeDryFire, sortBy]);

  const dryFireCount = useMemo(
    () => routines.filter(isDryFire).length,
    [routines]
  );

  const sortLabel =
    sortBy === 'score-desc' ? 'highest score first'
      : sortBy === 'score-asc' ? 'lowest score first'
        : 'newest first';

  const name = member ? (member.fullName || member.email) : '';

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="Member details">
        <div className="drawer-head">
          <div className="dh-title">
            <h2>{loading ? 'Loading…' : name}</h2>
            {member && <p>{member.email}</p>}
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading ? (
          <div className="wrap" style={{ display: 'grid', placeItems: 'center', minHeight: '40vh', color: 'var(--text-muted)' }}><Spinner size={30} /></div>
        ) : error === 'not_found' ? (
          <div className="wrap"><p className="muted">That member isn’t in your organization.</p></div>
        ) : error || !member ? (
          <div className="wrap"><p className="muted">Couldn’t load this member.</p></div>
        ) : (
          <div className="wrap" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div className="mhead">
              <span className="avatar" style={{ width: 56, height: 56, fontSize: 20, background: avatarColor(name) }}>{initials(member.fullName, member.email)}</span>
              <div className="mh-txt">
                <h1>{name}</h1>
                <p>
                  {member.sessionCount} session{member.sessionCount === 1 ? '' : 's'} · last active {timeAgo(member.lastSessionAt)}
                  {member.avgScore != null ? ` · avg score ${member.avgScore}` : ''}
                  {member.bestScore != null ? ` · best ${member.bestScore}` : ''} · joined {fmtDate(member.createdAt)}
                </p>
              </div>
            </div>

            <section className="card" aria-label="Session history">
              <div className="card-head">
                <h2>Session history</h2>
                <span className="sub">
                  {filteredRoutines.length}
                  {filteredRoutines.length !== routines.length ? ` of ${routines.length}` : ''} saved · {sortLabel}
                </span>
                <span className="spacer" />
                <label className="dry-filter">
                  Sort
                  <select
                    className="sess-sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Sort sessions"
                  >
                    <option value="newest">Newest</option>
                    <option value="score-desc">Score · high to low</option>
                    <option value="score-asc">Score · low to high</option>
                  </select>
                </label>
                <label className="dry-filter">
                  <input
                    type="checkbox"
                    checked={includeDryFire}
                    onChange={(e) => setIncludeDryFire(e.target.checked)}
                  />
                  Include DryFire
                  {dryFireCount > 0 ? ` (${dryFireCount})` : ''}
                </label>
              </div>
              <div style={{ padding: 18 }}>
                {routines.length === 0 ? (
                  <p className="muted" style={{ margin: '8px 4px' }}>No sessions saved yet.</p>
                ) : filteredRoutines.length === 0 ? (
                  <p className="muted" style={{ margin: '8px 4px' }}>No live-fire sessions to show. Enable Include DryFire to see dry practice.</p>
                ) : (
                  <div className="sess-grid">
                    {filteredRoutines.map((r) => {
                      const imgs = sessionImages(r);
                      const score = scoreOf(r);
                      return (
                        <button key={r.id} className="sess-card" onClick={() => setSelected(r)}>
                          <div className="thumb">{imgs[0] ? <img src={imgs[0]} alt="" /> : <span className="noimg">No photo</span>}</div>
                          <div className="body">
                            {score != null && <span className="score-chip">{score}</span>}
                            <div className="s-name">{r.name}</div>
                            <div className="s-meta">
                              <span className="lvl">{r.level}</span>
                              {isDryFire(r) && !/dry[\s_-]?fire/i.test(String(r.level || '')) && (
                                <span className="lvl dry">DryFire</span>
                              )}
                              <span>{fmtDate(r.started_at)}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </aside>

      {selected && (
        <div className="overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h2>{selected.name}</h2>
                <p>
                  {selected.level} · {fmtDate(selected.started_at)}
                  {selected.skills ? ` · ${selected.skills}` : ''}
                  {isDryFire(selected) && !/dry[\s_-]?fire/i.test(String(selected.level || '')) ? ' · DryFire' : ''}
                </p>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {sessionImages(selected).length > 0 && (
                <div>
                  <div className="sec-label" style={{ marginBottom: 8 }}>Photos</div>
                  <div className="photos">{sessionImages(selected).map((src, i) => <img key={i} src={src} alt="" onClick={() => setLightbox(src)} />)}</div>
                </div>
              )}
              {scoreOf(selected) != null && <div className="score-big"><span className="sb-num">{scoreOf(selected)}<small> / 100</small></span></div>}
              <AnalysisBlock analysis={selected.analysis} />
              {Array.isArray(selected.routine_drills) && selected.routine_drills.length > 0 && (
                <div>
                  <div className="sec-label" style={{ marginBottom: 8 }}>Drills</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {selected.routine_drills.map((d, i) => (
                      <div className="drill" key={i}>
                        <div className="d-top">
                          <b>{d.drillName || `Drill ${i + 1}`}</b>
                          {d.analysis && typeof d.analysis.score === 'number' && <span className="score-chip" style={{ position: 'static' }}>{d.analysis.score}</span>}
                        </div>
                        <div className="d-meta">
                          {(d.totalLiveRounds != null) ? `${d.totalLiveRounds} rounds` : ''}
                          {(d.selectedMags != null) ? ` · ${d.selectedMags} mag${d.selectedMags === 1 ? '' : 's'}` : ''}
                          {(d.selectedCycles != null) ? ` · ${d.selectedCycles} cycle${d.selectedCycles === 1 ? '' : 's'}` : ''}
                        </div>
                        {d.analysis && <div style={{ marginTop: 10 }}><AnalysisBlock analysis={d.analysis} /></div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {lightbox && <div className="lightbox" onClick={() => setLightbox(null)}><img src={lightbox} alt="" /></div>}
    </>
  );
}
